import React from "react";
import { motion } from "framer-motion";
import { Quote } from "lucide-react";
import { useLang } from "@/contexts/LangContext";

// Sample quotes: replace with real client testimonials before relying on them.
const COPY = {
  en: {
    kicker: "What companies say",
    title: "Clear answers, before the first invoice.",
    items: [
      { q: "We settled a notice-period question in ten minutes instead of waiting a week for a lawyer's reply.", who: "HR Manager", org: "Retail group, Beirut" },
      { q: "The NSSF and end-of-service calculations alone saved our finance team hours every month.", who: "Finance Director", org: "Manufacturing SME, Mount Lebanon" },
      { q: "We now arrive at our law firm with the right questions, so meetings are shorter and cheaper.", who: "Co-founder", org: "Tech startup, Beirut" },
    ],
  },
  fr: {
    kicker: "Ce que disent les entreprises",
    title: "Des réponses claires, avant la première facture.",
    items: [
      { q: "Nous avons réglé une question de préavis en dix minutes au lieu d'attendre une semaine la réponse d'un avocat.", who: "Responsable RH", org: "Groupe de distribution, Beyrouth" },
      { q: "Les calculs CNSS et d'indemnité de fin de service font gagner des heures à notre équipe finance chaque mois.", who: "Directeur financier", org: "PME industrielle, Mont-Liban" },
      { q: "Nous arrivons chez notre cabinet avec les bonnes questions : réunions plus courtes et moins coûteuses.", who: "Cofondatrice", org: "Startup tech, Beyrouth" },
    ],
  },
  ar: {
    kicker: "ماذا تقول الشركات",
    title: "إجابات واضحة، قبل أول فاتورة.",
    items: [
      { q: "حسمنا مسألة مهلة الإنذار في عشر دقائق بدلاً من انتظار ردّ المحامي لأسبوع.", who: "مديرة الموارد البشرية", org: "مجموعة تجزئة، بيروت" },
      { q: "حسابات الضمان الاجتماعي وتعويض نهاية الخدمة وحدها توفّر على فريقنا المالي ساعات كل شهر.", who: "المدير المالي", org: "شركة صناعية صغيرة، جبل لبنان" },
      { q: "صرنا نذهب إلى مكتب المحاماة بالأسئلة الصحيحة، فأصبحت الاجتماعات أقصر وأقل كلفة.", who: "شريكة مؤسِّسة", org: "شركة ناشئة تقنية، بيروت" },
    ],
  },
};

export const Testimonials = () => {
  const { lang } = useLang();
  const c = COPY[lang] || COPY.en;
  return (
    <section id="testimonials" className="bg-[#F8F9FA] border-t border-[#E9ECEF]" data-testid="testimonials-section">
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-24">
        <p className="text-xs tracking-[0.24em] uppercase font-bold text-[#C65D47]">{c.kicker}</p>
        <h2 className="mt-4 font-display text-4xl md:text-5xl leading-tight max-w-2xl">{c.title}</h2>
        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-[#E9ECEF] border border-[#E9ECEF]">
          {c.items.map((it, i) => (
            <motion.figure
              key={i}
              initial={{ opacity: 0, y: 16 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white p-8 md:p-10 flex flex-col justify-between hover:bg-[#FCFCFD] transition-colors"
              data-testid={`testimonial-${i}`}
            >
              <div>
                <Quote className="w-6 h-6 text-[#C65D47]" />
                <blockquote className="mt-6 font-display text-xl leading-snug text-[#0A1128]">“{it.q}”</blockquote>
              </div>
              <figcaption className="mt-8 pt-6 border-t border-[#E9ECEF]">
                <div className="text-sm font-medium text-[#0A1128]">{it.who}</div>
                <div className="text-xs text-[#868E96] mt-1">{it.org}</div>
              </figcaption>
            </motion.figure>
          ))}
        </div>
      </div>
    </section>
  );
};
